import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBrandingContext } from "../contexts/BrandingContext";
import { UserRole } from "../types";
import { useBalance } from "../contexts/BalanceContext";
import { getNotifications } from "../pages/company/Notifications/api/notificationApi";

const Navigation: React.FC = () => {
  const {
    user,
    company,
    logout,
    isSuperAdmin,
    isCompanyAdmin,
    canUploadResumes,
    canManageUsers,
    canManageJobs,
    hasProPlanAccess,
  } = useAuth();
  const { productName, logo, logoIcon } = useBrandingContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { balance, fetchBalance, trailStatus } = useBalance();
  const isRestricted =
    trailStatus != null &&
    !trailStatus.is_trial &&
    !trailStatus.has_subscription;

  useEffect(() => {
    if (user?.persona === "company_user") {
      fetchBalance();
      getNotifications({ is_read: false, limit: 1 })
        .then((res: any) => setUnreadCount(res?.total ?? 0))
        .catch(() => {});
    }
  }, [fetchBalance, user?.persona]);

  const handleLogout = () => {
    logout();
    navigate("/auth/login", { replace: true });
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleClearSession = () => {
    console.log("🧹 Manual session clear initiated...");

    // Clear all storage aggressively
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
    });

    // Clear caches
    if ("caches" in window) {
      caches.keys().then((cacheNames) => {
        return Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName)),
        );
      });
    }

    console.log("✅ Manual session clear completed");
    setIsProfileMenuOpen(false);

    // Force page reload to completely reset state
    window.location.href = "/auth/login";
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const getCompanyName = () => {
    return company?.name || "Your Company";
  };

  const getCompanyNameWithPlan = () => {
    const companyName = company?.name || "Your Company";
    const pricingPlan = company?.pricing_plan;
    const credits = balance?.balance ?? 0;

    if (!pricingPlan) {
      // Fallback to subscription_tier if no pricing plan
      const subscriptionTier = company?.subscription_tier;
      if (subscriptionTier) {
        const planName =
          subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1);
        return `${companyName} • ${planName} • ${credits} Credits`;
      }
      return `${companyName} • ${credits} Credits`;
    }

    // Use the actual pricing plan name from the database
    return `${companyName} • ${pricingPlan.name} • ${credits} Credits`;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const showProfileLink = user?.persona === "job_seeker";

  if (!user) return null;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center space-x-3 cursor-default select-none">
              <img
                src={logo}
                alt={`${productName} Logo`}
                className="h-8 w-auto"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.currentTarget.style.display = "none";
                  const nextElement = e.currentTarget
                    .nextElementSibling as HTMLElement;
                  if (nextElement) nextElement.style.display = "block";
                }}
              />
              <span
                className="text-2xl font-bold text-indigo-600"
                style={{ display: "none" }}
              >
                {productName}
              </span>
              {/* Company/Candidate Indicator - Shows data isolation */}
              {/* {!isSuperAdmin && user?.persona === "company_user" && ( */}
              {user?.persona === "company_user" && (
                <div
                  className={`ml-2 sm:ml-4 px-2 sm:px-3 py-1 rounded-full ${
                    user?.persona === "company_user"
                      ? "bg-indigo-50 border border-indigo-200"
                      : "bg-emerald-50 border border-emerald-200"
                  }`}
                >
                  <span
                    className={`text-xs font-medium ${
                      user?.persona === "company_user"
                        ? "text-indigo-700"
                        : "text-emerald-700"
                    }`}
                  >
                    <span className="hidden sm:inline">
                      {user?.persona === "company_user"
                        ? `🏢 ${getCompanyNameWithPlan()}`
                        : "👤 Candidate Profile"}
                    </span>
                    <span className="sm:hidden">
                      {user?.persona === "company_user" ? "🏢" : "👤"}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {isSuperAdmin ? (
                // Super Admin Navigation
                <>
                  <Link
                    to="/super-admin/dashboard"
                    className={`${
                      isActive("/super-admin/dashboard")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/super-admin/companies"
                    className={`${
                      isActive("/super-admin/companies")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Companies
                  </Link>
                  <Link
                    to="/super-admin/users"
                    className={`${
                      isActive("/super-admin/users")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Users
                  </Link>
                  <Link
                    to="/super-admin/finance"
                    className={`${
                      isActive("/super-admin/finance")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Finance
                  </Link>
                  <Link
                    to="/super-admin/job-seekers"
                    className={`${
                      isActive("/super-admin/job-seekers")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Job Seekers
                  </Link>
                  <Link
                    to="/super-admin/jobs"
                    className={`${
                      isActive("/super-admin/jobs")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Jobs
                  </Link>
                  <Link
                    to="/super-admin/subscriptions"
                    className={`${
                      isActive("/super-admin/subscriptions")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Subscriptions
                  </Link>
                  <Link
                    to="/super-admin/settings"
                    className={`${
                      isActive("/super-admin/settings")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Settings
                  </Link>
                  <Link
                    to="/super-admin/support-tickets"
                    className={`${
                      isActive("/super-admin/support-tickets")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Support
                  </Link>
                </>
              ) : user?.persona === "job_seeker" ? (
                // Candidate Navigation
                <>
                  <Link
                    to="/candidate/dashboard"
                    className={`${
                      isActive("/candidate/dashboard")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/candidate/profile"
                    className={`${
                      isActive("/candidate/profile")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/candidate/jobs"
                    className={`${
                      isActive("/candidate/jobs")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Jobs
                  </Link>
                  <Link
                    to="/candidate/notifications"
                    className={`${
                      isActive("/candidate/notifications")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/candidate/analytics"
                    className={`${
                      isActive("/candidate/analytics")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/candidate/messages"
                    className={`${
                      isActive("/candidate/messages")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Messages
                  </Link>
                  <Link
                    to="/candidate/profile-boost"
                    className={`${
                      isActive("/candidate/profile-boost")
                        ? "border-emerald-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium relative`}
                  >
                    Profile Boost
                    <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full">
                      ✨
                    </span>
                  </Link>
                </>
              ) : (
                // Company User Navigation
                <>
                  <Link
                    to="/company/dashboard"
                    className={`${
                      isActive("/company/dashboard")
                        ? "border-indigo-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    Dashboard
                  </Link>
                  {!isRestricted && (
                    <Link
                      to="/company/candidates"
                      className={`${
                        isActive("/company/candidates")
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      Candidates
                    </Link>
                  )}
                  {!isRestricted && (
                    <Link
                      to="/company/messages"
                      className={`${
                        isActive("/company/messages")
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      Messages
                    </Link>
                  )}

                  {!isRestricted && canManageJobs && hasProPlanAccess() && (
                    <Link
                      to="/company/jobs"
                      className={`${
                        isActive("/company/jobs")
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium relative`}
                    >
                      Jobs
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full">
                        PRO
                      </span>
                    </Link>
                  )}
                  {!isRestricted && canUploadResumes && (
                    <Link
                      to="/company/upload"
                      className={`${
                        isActive("/company/upload")
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      Upload Resumes
                    </Link>
                  )}
                  {canManageUsers && (
                    <Link
                      to="/company/users"
                      className={`${
                        isActive("/company/users")
                          ? "border-indigo-500 text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      Team
                    </Link>
                  )}
                  {user.role === UserRole.ADMIN && (
                    <>
                      <Link
                        to="/company/flowsetting"
                        className={`${
                          isActive("/company/flowsetting")
                            ? "border-indigo-500 text-gray-900"
                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                        } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                        onClick={closeMobileMenu}
                      >
                        Settings
                      </Link>
                    </>
                  )}

                  {/* Inline balance display */}
                </>
              )}
            </div>
            {user?.persona === "company_user" && (
              <Link
                to="/company/notifications"
                className={`${
                  isActive("/company/notifications")
                    ? "border-indigo-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                title="Notifications"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </Link>
            )}
          </div>

          {/* Desktop User Menu and Balance */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <div className="flex items-center space-x-4 mr-6">
              {/* <CreditBalanceView /> */}
              <div className="ml-3 relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="bg-white flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-indigo-700">
                      {user.first_name?.[0]}
                      {user.last_name?.[0]}
                    </span>
                  </div>
                </button>
                <div className="relative">
                  {isProfileMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                        <div className="font-medium">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-gray-500 break-all">
                          {user.email}
                        </div>
                        {!isSuperAdmin && user?.persona === "company_user" && (
                          <div className="text-xs text-indigo-600 mt-1">
                            🔒 Company Data Only
                          </div>
                        )}
                      </div>
                      {showProfileLink && (
                        <Link
                          to="/candidate/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Your Profile
                        </Link>
                      )}
                      {user?.persona === "job_seeker" && (
                        <>
                          <Link
                            to="/candidate/profile/edit"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            Edit Profile
                          </Link>
                          <Link
                            to="/candidate/account-settings"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            Account Settings
                          </Link>
                        </>
                      )}
                      {user?.persona === "company_user" && (
                        <Link
                          to="/company/change-password"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Change Password
                        </Link>
                      )}
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={handleClearSession}
                        className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                        title="Clear all browser storage and reset session (for debugging)"
                      >
                        🧹 Clear Session
                      </button>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center space-x-2">
            {/* Mobile Profile Avatar */}
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="bg-white flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 relative"
            >
              <span className="sr-only">Open user menu</span>
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-sm font-medium text-indigo-700">
                  {user.first_name?.[0]}
                  {user.last_name?.[0]}
                </span>
              </div>
            </button>

            {/* Mobile Profile Dropdown */}
            {isProfileMenuOpen && (
              <div className="origin-top-right absolute right-12 top-16 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                  <div className="font-medium">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-gray-500">{user.email}</div>
                  {!isSuperAdmin && user?.persona === "company_user" && (
                    <div className="text-xs text-indigo-600 mt-1">
                      🔒 Company Data Only
                    </div>
                  )}
                </div>
                {showProfileLink && (
                  <Link
                    to="/candidate/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Your Profile
                  </Link>
                )}
                {user?.persona === "job_seeker" && (
                  <>
                    <Link
                      to="/candidate/profile/edit"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      Edit Profile
                    </Link>
                    <Link
                      to="/candidate/account-settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      Account Settings
                    </Link>
                  </>
                )}
                {user?.persona === "company_user" && (
                  <Link
                    to="/company/change-password"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Change Password
                  </Link>
                )}
                <div className="border-t border-gray-100 my-1"></div>
                <button
                  onClick={handleClearSession}
                  className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                  title="Clear all browser storage and reset session (for debugging)"
                >
                  🧹 Clear Session
                </button>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Sign out
                </button>
              </div>
            )}

            {/* Hamburger Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1 border-t border-gray-200">
              {isSuperAdmin ? (
                // Super Admin Mobile Navigation
                <>
                  <Link
                    to="/super-admin/dashboard"
                    className={`${
                      isActive("/super-admin/dashboard")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/super-admin/companies"
                    className={`${
                      isActive("/super-admin/companies")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Companies
                  </Link>
                  <Link
                    to="/super-admin/users"
                    className={`${
                      isActive("/super-admin/users")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Users
                  </Link>
                  <Link
                    to="/super-admin/finance"
                    className={`${
                      isActive("/super-admin/finance")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Finance
                  </Link>
                  <Link
                    to="/super-admin/job-seekers"
                    className={`${
                      isActive("/super-admin/job-seekers")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Job Seekers
                  </Link>
                  <Link
                    to="/super-admin/subscriptions"
                    className={`${
                      isActive("/super-admin/subscriptions")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Subscriptions
                  </Link>
                  <Link
                    to="/super-admin/settings"
                    className={`${
                      isActive("/super-admin/settings")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Settings
                  </Link>
                  <Link
                    to="/super-admin/support-tickets"
                    className={`${
                      isActive("/super-admin/support-tickets")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Support
                  </Link>
                </>
              ) : user?.persona === "job_seeker" ? (
                // Candidate Mobile Navigation
                <>
                  <Link
                    to="/candidate/dashboard"
                    className={`${
                      isActive("/candidate/dashboard")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/candidate/profile"
                    className={`${
                      isActive("/candidate/profile")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/candidate/jobs"
                    className={`${
                      isActive("/candidate/jobs")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Jobs
                  </Link>
                  <Link
                    to="/candidate/notifications"
                    className={`${
                      isActive("/candidate/notifications")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/candidate/analytics"
                    className={`${
                      isActive("/candidate/analytics")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/candidate/messages"
                    className={`${
                      isActive("/candidate/messages")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Messages
                  </Link>
                  <Link
                    to="/candidate/profile-boost"
                    className={`${
                      isActive("/candidate/profile-boost")
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Profile Boost
                  </Link>
                </>
              ) : (
                // Company User Mobile Navigation
                <>
                  <Link
                    to="/company/dashboard"
                    className={`${
                      isActive("/company/dashboard")
                        ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                        : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                    onClick={closeMobileMenu}
                  >
                    Dashboard
                  </Link>
                  {!isRestricted && (
                    <Link
                      to="/company/candidates"
                      className={`${
                        isActive("/company/candidates")
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                      onClick={closeMobileMenu}
                    >
                      Candidates
                    </Link>
                  )}
                  {!isRestricted && (
                    <Link
                      to="/company/messages"
                      className={`${
                        isActive("/company/messages")
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                      onClick={closeMobileMenu}
                    >
                      Messages
                    </Link>
                  )}
                  {!isRestricted && canManageJobs && hasProPlanAccess() && (
                    <Link
                      to="/company/jobs"
                      className={`${
                        isActive("/company/jobs")
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                      onClick={closeMobileMenu}
                    >
                      Jobs
                    </Link>
                  )}
                  {!isRestricted && canUploadResumes && (
                    <Link
                      to="/company/upload"
                      className={`${
                        isActive("/company/upload")
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                      onClick={closeMobileMenu}
                    >
                      Upload Resumes
                    </Link>
                  )}
                  {canManageUsers && (
                    <Link
                      to="/company/users"
                      className={`${
                        isActive("/company/users")
                          ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                      onClick={closeMobileMenu}
                    >
                      Team
                    </Link>
                  )}
                  {user.role === UserRole.ADMIN && (
                    <>
                      <Link
                        to="/company/flowsetting"
                        className={`${
                          isActive("/company/users")
                            ? "bg-indigo-50 border-indigo-500 text-indigo-700"
                            : "border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                        } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                        onClick={closeMobileMenu}
                      >
                        Settings
                      </Link>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
